/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Calendar';
import { page as SamplePage } from '../../help/sample_pages/CalendarPage';

const CalendarManager: ComponentManagerFn<'Calendar'> = (
  platform: Platform,
) => {
  const callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'Calendar'>(platform, 'Calendar'),
    type: 'Calendar',
    name: 'Calendar',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnEventChange',
          'TriggerOnEventClick',
          'TriggerOnEventCreate',
          'TriggerOnDrop',
          'TriggerOnDateChange',
          'Popover',
          'Box',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
      },
      fromDateAttribute: {
        label: 'From Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Date', 'DateTime', 'StringDateTime'],
        preventMultiEdit: true,
        required: true,
      },
      toDateAttribute: {
        label: 'To Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Date', 'DateTime', 'StringDateTime'],
        preventMultiEdit: true,
        required: true,
      },
      titleAttribute: {
        label: 'Title Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        required: true,
      },
      allDayAttribute: {
        label: 'All Day Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Boolean'],
        preventMultiEdit: true,
        required: true,
        helpText: 'Must be a boolean attribute',
      },
      allDayText: {
        label: 'All Day Label Text',
        type: 'string',
        category: 'Data',
        helpText:
          'This text will be displayed for all-day events in the calendar.',
      },
      hideAllDaySlot: {
        label: 'Hide All Day Slot',
        type: 'boolean',
        category: 'Data',
      },
      hideTimeSlot: {
        label: 'Hide Time Slot',
        type: 'boolean',
        category: 'Data',
      },
      showOnlyAllDaySlot: {
        label: 'Show Only All Day Slot',
        type: 'boolean',
        category: 'Data',
      },
      showYearMonthPicker: {
        label: 'Show Year and Month Picker',
        type: 'boolean',
        category: 'Data',
      },
      createEventOnClick: {
        label: 'Create Event On Click',
        type: 'boolean',
        category: 'Data',
      },
      month: {
        label: 'Month',
        type: 'boolean',
        category: 'Data',
      },
      week: {
        label: 'Week',
        type: 'boolean',
        category: 'Data',
      },
      day: {
        label: 'Day',
        type: 'boolean',
        category: 'Data',
      },
      list: {
        label: 'List',
        type: 'boolean',
        category: 'Data',
      },
      today: {
        label: 'Today',
        type: 'boolean',
        category: 'Data',
      },
      showPagination: {
        label: 'Show Pagination',
        type: 'boolean',
        category: 'Data',
      },
      disablePastNavigation: {
        label: 'Disable Past Navigation',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to disable navigation to Past Month or Week.',
      },
      renderCount: {
        label: 'Render Count',
        type: 'number',
        hideInput: true,
        category: 'Data',
        helpText:
          "Provide a value to restrict the number of events per batch. It'll divide the total events rendering into batches based on count.",
      },
      dayMaxEvents: {
        label: 'Maximum Events per Day',
        type: 'number',
        category: 'Data',
        helpText:
          'Sets the maximum number of events shown in each day cell. When the number of events exceeds this value, a "+ more" link appears, allowing users to expand and view all events for that day. Accepts a number (e.g., 3) to limit events per cell.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'fromDateAttribute':
        case 'toDateAttribute':
        case 'titleAttribute':
        case 'allDayAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'hideTimeSlot':
        case 'hideAllDaySlot':
          return !itemData.showOnlyAllDaySlot;
        case 'allDayText':
          return !itemData.hideAllDaySlot;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value) => {
      if (property === 'showOnlyAllDaySlot') {
        callback();
        return {
          [property]: value,
          hideTimeSlot: defaultValue.hideTimeSlot,
          hideAllDaySlot: defaultValue.hideAllDaySlot,
        };
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) => item.props.fromDateAttribute,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'CALENDAR'
        ? { aliasProp: 'comboDatasourceAlias' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    category: 'Data Containers',
    icon: { iconName: 'calendar' },
    overlayZIndex: 1,
    description: `Calendar is used to represent Events.

The DataSource representing events must have attributes to hold the following data

1. Title Attribute - A String type attribute to hold the event's title.
2. From & To Date Attributes - Two Date Time type attributes to hold the start and end times of an event
3. All Day Attribute - A Boolean type attribute to hold the flag indicating all day event

Use one of the applicable triggers to customize/enable event creation.
`,
    helpRenders: [
      {
        title: 'Sample Calendar',
        description: 'Calendar rendered with differnt type of events',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        height: 800,
        item: {
          children: [],
          itemId: 'calendar',
          parent: 'flexItem',
          props: {
            allDayAttribute: 'allDay',
            comboDatasourceAlias: 'test',
            fromDateAttribute: 'fromDate',
            titleAttribute: 'title',
            toDateAttribute: 'toDate',
            allDayText: 'all-day',
          },
          type: 'Calendar',
        },
      },
    ],
  };
};

export default CalendarManager;
